/* eslint-disable no-restricted-globals, no-alert */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAllQuestions, deleteQuestion, QuestionMaster, getExamIdFromType, bulkInsertQuestions, getCategoryLabel, updateQuestionsSet } from '../utils/questionApi';

type ExamType = { value: string; label: string; emoji: string };

const EXAM_TYPES: ExamType[] = [
  { value: 'all', label: 'All Exams', emoji: '📚' },
  { value: 'saa', label: 'AWS Solutions Architect (SAA-C03)', emoji: '🏗️' },
  { value: 'sap', label: 'AWS Solutions Architect Professional (SAP-C02)', emoji: '🎓' },
  { value: 'ccp', label: 'AWS Cloud Practitioner (CCP)', emoji: '☁️' },
  { value: 'dva', label: 'AWS Developer Associate (DVA)', emoji: '💻' },
  { value: 'sysdesign', label: 'System Design', emoji: '⚙️' },
  { value: 'dsa', label: 'Data Structures & Algorithms', emoji: '🧩' },
  { value: 'python', label: 'Python', emoji: '🐍' },
  { value: 'dotnet', label: '.NET / Java', emoji: '🔷' },
  { value: 'aiml', label: 'AI & Machine Learning', emoji: '🤖' },
];

const getExamTypeFromId = (examId: number): string => {
  const examTypeMap: Record<number, string> = {
    1: 'saa',
    2: 'sap',
    3: 'ccp',
    4: 'dva',
    5: 'sysdesign',
    6: 'dsa',
    7: 'python',
    8: 'dotnet',
    9: 'aiml',
  };
  return examTypeMap[examId] || 'saa';
};

const ManageQuestionsPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem('adminAuth') !== '1') {
      navigate('/admin-login');
    }
  }, [navigate]);

  const [questions, setQuestions] = useState<QuestionMaster[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<QuestionMaster[]>([]);
  const [filterExam, setFilterExam] = useState('all');
  const [filterSet, setFilterSet] = useState('all');
  const [filterTopic, setFilterTopic] = useState('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkSet, setBulkSet] = useState('');
  const [groupSize, setGroupSize] = useState('25');
  const [groupStart, setGroupStart] = useState('1');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState<QuestionMaster[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadQuestions();
  }, []);

  const toggleCategory = (examId: number) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(examId)) next.delete(examId);
      else next.add(examId);
      return next;
    });
  };

  const toggleQuestion = (questionId: number) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };

  const loadQuestions = async () => {
    setLoading(true);
    const result = await fetchAllQuestions();
    if (result.success && result.data) {
      setQuestions(result.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    let filtered = [...questions];

    if (filterExam !== 'all') {
      const examId = getExamIdFromType(filterExam);
      filtered = filtered.filter(q => q.exam_id === examId);
    }

    if (filterTopic !== 'all') {
      filtered = filtered.filter(q => q.category_id === parseInt(filterTopic, 10));
    }

    if (filterSet === 'none') {
      filtered = filtered.filter(q => q.set === null || q.set === undefined);
    } else if (filterSet !== 'all') {
      filtered = filtered.filter(q => q.set === parseInt(filterSet, 10));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(q =>
        q.question.toLowerCase().includes(query) ||
        (q.option1 || '').toLowerCase().includes(query) ||
        (q.option2 || '').toLowerCase().includes(query) ||
        (q.option3 || '').toLowerCase().includes(query) ||
        (q.option4 || '').toLowerCase().includes(query) ||
        (q.explaination || '').toLowerCase().includes(query)
      );
    }

    filtered.sort((a, b) => (b.id || 0) - (a.id || 0));
    setFilteredQuestions(filtered);
  }, [questions, filterExam, filterTopic, filterSet, searchQuery]);

  // Sets that exist for the currently selected exam (all exams when "All Exams")
  const examScopedQuestions = filterExam === 'all' ? questions : questions.filter(q => q.exam_id === getExamIdFromType(filterExam));
  const availableSets = Array.from(new Set(examScopedQuestions.map(q => q.set).filter((x): x is number => x !== null && x !== undefined))).sort((a, b) => a - b);
  // Topics (category ids) that exist for the selected exam; only offered once a single exam is chosen
  const availableTopics = filterExam === 'all'
    ? []
    : Array.from(new Set(examScopedQuestions.map(q => q.category_id))).sort((a, b) => a - b);
  const topicCount = (id: number) => examScopedQuestions.filter(q => q.category_id === id).length;
  const setCount = (n: number) => examScopedQuestions.filter(q => q.set === n).length;
  const noSetCount = examScopedQuestions.filter(q => q.set === null || q.set === undefined).length;

  const groupedQuestions = filteredQuestions.reduce((acc, q) => {
    const examId = q.exam_id;
    if (!acc[examId]) acc[examId] = [];
    acc[examId].push(q);
    return acc;
  }, {} as Record<number, QuestionMaster[]>);

  const selectedVisible = filteredQuestions.filter(q => selected.has(q.id!));

  const toggleSelected = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applySetLocally = (ids: number[], set: number | null) => {
    const idSet = new Set(ids);
    setQuestions(prev => prev.map(q => (idSet.has(q.id!) ? { ...q, set } : q)));
  };

  const handleAssignSet = async (set: number | null) => {
    if (selectedVisible.length === 0) return;
    if (set !== null && (!Number.isInteger(set) || set < 1)) { alert('Enter a set number of 1 or more.'); return; }
    const label = set === null ? 'remove the set from' : `move to Set ${set}`;
    if (!confirm(`This will ${label} ${selectedVisible.length} question(s). Continue?`)) return;
    setBulkBusy(true);
    const ids = selectedVisible.map(q => q.id!);
    const res = await updateQuestionsSet(ids, set);
    setBulkBusy(false);
    if (!res.success) { alert('Failed to update sets: ' + res.error); return; }
    applySetLocally(ids, set);
    setSelected(new Set());
  };

  // Split the questions currently shown into consecutive sets (oldest first): size per set, numbering from "start"
  const handleAutoGroup = async () => {
    const size = parseInt(groupSize, 10);
    const start = parseInt(groupStart, 10);
    if (!(size >= 1) || !(start >= 1)) { alert('Enter a set size and a starting set number of 1 or more.'); return; }
    if (filterExam === 'all') { alert('Choose a single exam first so questions from different exams are not mixed.'); return; }
    const ordered = [...filteredQuestions].sort((a, b) => (a.id || 0) - (b.id || 0));
    const groups = Math.ceil(ordered.length / size);
    if (!confirm(`Split the ${ordered.length} questions shown into ${groups} set(s) of up to ${size}, numbered ${start} to ${start + groups - 1}? Existing set numbers on these questions will be replaced.`)) return;
    setBulkBusy(true);
    for (let g = 0; g < groups; g++) {
      const ids = ordered.slice(g * size, (g + 1) * size).map(q => q.id!);
      const res = await updateQuestionsSet(ids, start + g);
      if (!res.success) {
        setBulkBusy(false);
        alert(`Stopped at Set ${start + g}: ${res.error}`);
        loadQuestions();
        return;
      }
      applySetLocally(ids, start + g);
    }
    setBulkBusy(false);
    setSelected(new Set());
  };

  const handleDelete = async (questionId: number) => {
    if (confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      const result = await deleteQuestion(questionId);
      if (result.success) {
        setQuestions(questions.filter(q => q.id !== questionId));
      } else {
        alert('Failed to delete question: ' + result.error);
      }
      setDeleteConfirm(null);
    }
  };

  const handleEdit = (questionId: number) => {
    navigate(`/admin/add-question?edit=${questionId}&from=db`);
  };

  const getExamLabel = (examId: number): string => {
    const examType = getExamTypeFromId(examId);
    const exam = EXAM_TYPES.find(e => e.value === examType);
    return exam ? `${exam.emoji} ${exam.label}` : `Exam ${examId}`;
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  // Helper to format JSON and tables in question text
  const formatQuestionText = (text: string): string => {
    if (!text) return '';
    
    let result = text;
    
    // Try to find and format JSON content
    const jsonRegex = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;
    result = result.replace(jsonRegex, (match) => {
      try {
        const formatted = JSON.stringify(JSON.parse(match), null, 2);
        return '<pre style="background: var(--gray-100); padding: 12px; border-radius: 6px; overflow-x: auto; font-size: 0.85rem; margin: 8px 0;">' + 
               formatted.replace(/\n/g, '<br>').replace(/ /g, '&nbsp;') + '</pre>';
      } catch {
        return match;
      }
    });
    
    // Handle explicit table markers [TABLE]...[/TABLE]
    result = result.replace(/\[TABLE\]([\s\S]*?)\[\/TABLE\]/g, (match, tableContent) => {
      const tableLines = tableContent.trim().split(/\n/);
      let explicitHeaders: string[] = [];
      let explicitRows: string[][] = [];
      
      for (const line of tableLines) {
        const tabs = line.split('\t');
        if (tabs.length >= 2 && tabs.some((t: string) => t.trim().length > 0)) {
          if (explicitHeaders.length === 0) {
            explicitHeaders = tabs.map((t: string) => t.trim());
            explicitRows = [];
          } else {
            explicitRows.push(tabs.map((t: string) => t.trim()));
          }
        }
      }
      
      if (explicitHeaders.length > 0) {
        return '<table style="width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 0.85rem;">' +
          '<thead><tr>' + explicitHeaders.map(h => 
            '<th style="border: 1px solid var(--gray-300); padding: 8px; background: var(--gray-100); text-align: left;">' + h + '</th>'
          ).join('') + '</tr></thead>' +
          '<tbody>' + explicitRows.map(row => 
            '<tr>' + row.map(cell => 
              '<td style="border: 1px solid var(--gray-300); padding: 8px;">' + cell + '</td>'
            ).join('') + '</tr>'
          ).join('') + '</tbody></table>';
      }
      return match;
    });
    
    // Convert URLs to clickable links
    result = result.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: var(--blue);">$1</a>');
    
    // Try to find and format table content (tab-separated values)
    const textLines = result.split(/\n/);
    const outputLines: string[] = [];
    let implicitHeaders: string[] = [];
    let implicitRows: string[][] = [];
    
    for (let i = 0; i < textLines.length; i++) {
      const line = textLines[i];
      const tabs = line.split('\t');
      
      // Check if this looks like a table row (has tabs and multiple columns)
      if (tabs.length >= 2 && tabs.some((t: string) => t.trim().length > 0)) {
        if (implicitHeaders.length === 0) {
          // First row is header
          implicitHeaders = tabs.map((t: string) => t.trim());
          implicitRows = [];
        } else {
          implicitRows.push(tabs.map((t: string) => t.trim()));
        }
      } else {
        // Not a table row, output any accumulated table first
        if (implicitHeaders.length > 0) {
          const tableHtml = '<table style="width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 0.85rem;">' +
            '<thead><tr>' + implicitHeaders.map(h => 
              '<th style="border: 1px solid var(--gray-300); padding: 8px; background: var(--gray-100); text-align: left;">' + h + '</th>'
            ).join('') + '</tr></thead>' +
            '<tbody>' + implicitRows.map(row => 
              '<tr>' + row.map(cell => 
                '<td style="border: 1px solid var(--gray-300); padding: 8px;">' + cell + '</td>'
              ).join('') + '</tr>'
            ).join('') + '</tbody></table>';
          outputLines.push(tableHtml);
          implicitHeaders = [];
          implicitRows = [];
        }
        outputLines.push(line);
      }
    }
    
    // Handle any remaining table at end
    if (implicitHeaders.length > 0) {
      const tableHtml = '<table style="width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 0.85rem;">' +
        '<thead><tr>' + implicitHeaders.map(h => 
          '<th style="border: 1px solid var(--gray-300); padding: 8px; background: var(--gray-100); text-align: left;">' + h + '</th>'
        ).join('') + '</tr></thead>' +
        '<tbody>' + implicitRows.map(row => 
          '<tr>' + row.map(cell => 
            '<td style="border: 1px solid var(--gray-300); padding: 8px;">' + cell + '</td>'
          ).join('') + '</tr>'
        ).join('') + '</tbody></table>';
      outputLines.push(tableHtml);
    }
    
    return outputLines.join('\n');
  };


  const getCorrectAnswerLabel = (correctAns: string): string => {
    if (!correctAns) return '-';
    return correctAns.split(',').map(i => {
      const idx = parseInt(i, 10) - 1;
      return String.fromCharCode(65 + idx);
    }).join(', ');
  };

  const getOption = (q: QuestionMaster, index: number): string => {
    const options = [q.option1, q.option2, q.option3, q.option4, q.option5, q.option6];
    return options[index] || '';
  };

  const handleExport = () => {
    const questionsToExport = filteredQuestions.length > 0 ? filteredQuestions : questions;
    if (questionsToExport.length === 0) {
      alert('No questions to export');
      return;
    }

    const headers = ['exam_id', 'category_id', 'question', 'option1', 'option2', 'option3', 'option4', 'option5', 'option6', 'total_option', 'correct_ans', 'explaination', 'set'];
    const csvRows = [headers.join(',')];

    questionsToExport.forEach(q => {
      const row = [
        q.exam_id,
        q.category_id,
        `"${(q.question || '').replace(/"/g, '""')}"`,
        `"${(q.option1 || '').replace(/"/g, '""')}"`,
        `"${(q.option2 || '').replace(/"/g, '""')}"`,
        `"${(q.option3 || '').replace(/"/g, '""')}"`,
        `"${(q.option4 || '').replace(/"/g, '""')}"`,
        `"${(q.option5 || '').replace(/"/g, '""')}"`,
        `"${(q.option6 || '').replace(/"/g, '""')}"`,
        q.total_option,
        `"${(q.correct_ans || '').replace(/"/g, '""')}"`,
        `"${(q.explaination || '').replace(/"/g, '""')}"`,
        q.set ?? ''
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `questions_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            setImportError('CSV file is empty or has no data rows');
            return;
          }

          const headerCols = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
          const setCol = headerCols.indexOf('set') >= 0 ? headerCols.indexOf('set') : 12;
          const parsedQuestions: QuestionMaster[] = [];
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i];
            const values: string[] = [];
            let current = '';
            let inQuotes = false;
            for (let j = 0; j < line.length; j++) {
              const char = line[j];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim());

            if (values.length >= 10) {
              parsedQuestions.push({
                exam_id: parseInt(values[0]) || 1,
                category_id: parseInt(values[1]) || 1,
                question: values[2] || '',
                option1: values[3] || '',
                option2: values[4] || '',
                option3: values[5] || '',
                option4: values[6] || '',
                option5: values[7] || '',
                option6: values[8] || '',
                total_option: parseInt(values[9]) || 4,
                correct_ans: values[10] || '1',
                explaination: values[11] || '',
                set: values[setCol] && !isNaN(parseInt(values[setCol], 10)) ? parseInt(values[setCol], 10) : null
              });
            }
          }

          if (parsedQuestions.length === 0) {
            setImportError('No valid questions found in CSV file');
            return;
          }

          setImportData(parsedQuestions);
          setImportError(null);
          setShowImportModal(true);
        } catch (err) {
          setImportError('Failed to parse CSV file: ' + String(err));
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleConfirmImport = async () => {
    setImporting(true);
    const result = await bulkInsertQuestions(importData);
    setImporting(false);

    if (result.success) {
      alert(`Successfully imported ${result.insertedCount} questions!`);
      setShowImportModal(false);
      setImportData([]);
      loadQuestions();
    } else {
      alert('Failed to import questions: ' + result.error);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportData([]);
    setImportError(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)', padding: '20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <Link
              to="/admin"
              style={{ fontSize: '0.9rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none', marginBottom: '8px' }}
            >
              ← Back to Admin
            </Link>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gray-800)' }}>
              📝 Manage Questions (Database)
            </h1>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={handleExport} className="btn btn-green" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              📤 Export
            </button>
            <button onClick={handleImportClick} className="btn btn-orange" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              📥 Import
            </button>
            <Link to="/admin/add-question" className="btn btn-blue" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}>
              ➕ Add New Question
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '4px' }}>Total Questions</div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--gray-800)' }}>{questions.length}</div>
          </div>
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '4px' }}>Showing</div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--blue)' }}>{filteredQuestions.length}</div>
          </div>
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '4px' }}>Exam Types</div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--green)' }}>{new Set(questions.map(q => q.exam_id)).size}</div>
          </div>
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '4px' }}>Sets{filterExam !== 'all' ? ' (this exam)' : ''}</div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--orange)' }}>{availableSets.length}</div>
            {noSetCount > 0 && <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{noSetCount} without a set</div>}
          </div>
        </div>

        {/* Filters */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Filter by Exam
              </label>
              <select
                value={filterExam}
                onChange={e => { setFilterExam(e.target.value); setFilterTopic('all'); setFilterSet('all'); setSelected(new Set()); }}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem' }}
              >
                {EXAM_TYPES.map(exam => (
                  <option key={exam.value} value={exam.value}>{exam.emoji} {exam.label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: '1', minWidth: '180px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Filter by Topic
              </label>
              <select
                value={filterTopic}
                onChange={e => { setFilterTopic(e.target.value); setSelected(new Set()); }}
                disabled={filterExam === 'all'}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem' }}
              >
                <option value="all">{filterExam === 'all' ? 'Select an exam first' : 'All topics'}</option>
                {availableTopics.map(id => (
                  <option key={id} value={String(id)}>{getCategoryLabel(getExamIdFromType(filterExam), id)} ({topicCount(id)})</option>
                ))}
              </select>
            </div>
            <div style={{ flex: '1', minWidth: '160px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Filter by Set
              </label>
              <select
                value={filterSet}
                onChange={e => { setFilterSet(e.target.value); setSelected(new Set()); }}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem' }}
              >
                <option value="all">All sets</option>
                {availableSets.map(n => (
                  <option key={n} value={String(n)}>Set {n} ({setCount(n)})</option>
                ))}
                <option value="none">No set ({noSetCount})</option>
              </select>
            </div>
            <div style={{ flex: '2', minWidth: '200px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Search Questions
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search in questions, options, or explanation..."
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem' }}
              />
            </div>
          </div>
        </div>

        {/* Bulk set tools */}
        {!loading && filteredQuestions.length > 0 && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px', display: 'flex', flexWrap: 'wrap', gap: '14px 24px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '0.9rem' }}>{selectedVisible.length} selected</strong>
              <button className="btn btn-outline-blue btn-sm" onClick={() => setSelected(new Set(filteredQuestions.map(q => q.id!)))}>Select all shown ({filteredQuestions.length})</button>
              <button className="btn btn-outline-blue btn-sm" disabled={selectedVisible.length === 0} onClick={() => setSelected(new Set())}>Clear</button>
              <input
                type="number" min={1} value={bulkSet} onChange={e => setBulkSet(e.target.value)} placeholder="Set no."
                style={{ width: '90px', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
              />
              <button className="btn btn-orange btn-sm" disabled={bulkBusy || selectedVisible.length === 0 || !bulkSet} onClick={() => handleAssignSet(parseInt(bulkSet, 10))}>Move to set</button>
              <button className="btn btn-outline-blue btn-sm" disabled={bulkBusy || selectedVisible.length === 0} onClick={() => handleAssignSet(null)}>Remove set</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', borderLeft: '1px solid var(--gray-200)', paddingLeft: '20px' }}>
              <strong style={{ fontSize: '0.9rem' }}>Auto-group shown:</strong>
              <label style={{ fontSize: '0.85rem' }}>per set
                <input type="number" min={1} value={groupSize} onChange={e => setGroupSize(e.target.value)} style={{ width: '70px', marginLeft: '6px', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: '6px' }} />
              </label>
              <label style={{ fontSize: '0.85rem' }}>start at set
                <input type="number" min={1} value={groupStart} onChange={e => setGroupStart(e.target.value)} style={{ width: '70px', marginLeft: '6px', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: '6px' }} />
              </label>
              <button className="btn btn-blue btn-sm" disabled={bulkBusy || filterExam === 'all'} onClick={handleAutoGroup} title={filterExam === 'all' ? 'Choose an exam first' : ''}>
                {bulkBusy ? 'Working…' : 'Group into sets'}
              </button>
            </div>
          </div>
        )}

        {/* Questions List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
            Loading questions from database...
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📭</div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '8px' }}>No Questions Found</h3>
            <p style={{ color: 'var(--gray-500)', marginBottom: '20px' }}>
              {searchQuery || filterExam !== 'all' || filterTopic !== 'all' || filterSet !== 'all'
                ? 'Try adjusting your filters or search query.'
                : 'Start by adding your first question to the database.'}
            </p>
            <Link to="/admin/add-question" className="btn btn-blue" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}>
              ➕ Add First Question
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {Object.entries(groupedQuestions).map(([examId, examQuestions]) => {
              const examIdNum = parseInt(examId);
              const isExpanded = expandedCategories.has(examIdNum);
              const examLabel = getExamLabel(examIdNum);

              return (
                <div key={examId} style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  {/* Category Header */}
                  <div
                    onClick={() => toggleCategory(examIdNum)}
                    style={{
                      padding: '16px 20px',
                      background: 'var(--blue)',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderRadius: isExpanded ? '12px 12px 0 0' : '12px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '1.2rem' }}>{isExpanded ? '📂' : '📁'}</span>
                      <span style={{ fontWeight: 700, fontSize: '1rem' }}>{examLabel}</span>
                      <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>
                        {examQuestions.length} questions
                      </span>
                      {new Set(examQuestions.map(x => x.set).filter(x => x !== null && x !== undefined)).size > 0 && (
                        <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>
                          {new Set(examQuestions.map(x => x.set).filter(x => x !== null && x !== undefined)).size} {new Set(examQuestions.map(x => x.set).filter(x => x !== null && x !== undefined)).size === 1 ? 'set' : 'sets'}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '1.2rem' }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>

                  {/* Questions List */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid var(--gray-200)' }}>
                      {examQuestions.map((question) => {
                        const correctAnswers = question.correct_ans ? question.correct_ans.split(',') : [];
                        const isQuestionExpanded = expandedQuestions.has(question.id!);

                        return (
                          <div
                            key={question.id}
                            style={{
                              borderLeft: '4px solid var(--blue)',
                              borderBottom: '1px solid var(--gray-100)',
                              background: 'white'
                            }}
                          >
                            {/* Question Row - Always Visible */}
                            <div
                              onClick={() => toggleQuestion(question.id!)}
                              style={{
                                padding: '16px 20px',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: 'var(--gray-50)',
                                borderBottom: isQuestionExpanded ? '1px solid var(--gray-200)' : 'none'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={selected.has(question.id!)}
                                  onClick={e => e.stopPropagation()}
                                  onChange={() => toggleSelected(question.id!)}
                                  style={{ flexShrink: 0, width: '16px', height: '16px' }}
                                />
                                <span style={{ fontSize: '1.2rem' }}>
                                  {isQuestionExpanded ? '📖' : '📄'}
                                </span>
                                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--gray-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  #{question.id} — {truncateText(question.question.replace(/<[^>]*>/g, ''), 120)}
                                </span>
                                <span style={{ flexShrink: 0, padding: '2px 10px', borderRadius: '12px', fontSize: '0.74rem', fontWeight: 700, background: question.set !== null && question.set !== undefined ? 'var(--orange-pale)' : 'var(--gray-100)', color: question.set !== null && question.set !== undefined ? 'var(--orange)' : 'var(--gray-500)' }}>
                                  {question.set !== null && question.set !== undefined ? `Set ${question.set}` : 'No set'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: '12px' }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEdit(question.id!); }}
                                  style={{
                                    padding: '6px 12px',
                                    background: 'var(--blue)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDeleteConfirm(question.id!); }}
                                  style={{
                                    padding: '6px 12px',
                                    background: 'white',
                                    color: 'var(--red)',
                                    border: '1px solid var(--red)',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  🗑️ Delete
                                </button>
                                <span style={{ fontSize: '1.2rem', color: 'var(--gray-500)' }}>
                                  {isQuestionExpanded ? '▲' : '▼'}
                                </span>
                              </div>
                            </div>

                            {/* Expanded Content */}
                            {isQuestionExpanded && (
                              <div style={{ padding: '20px' }}>
                                {/* Full Question Text */}
                                <div style={{ marginBottom: '16px' }}>
                                  <div
                                    style={{ fontSize: '0.95rem', color: 'var(--gray-800)', fontWeight: 500, lineHeight: 1.6 }}
                                    dangerouslySetInnerHTML={{ __html: formatQuestionText(question.question) }}
                                  />
                                </div>

                                {/* Options */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', marginBottom: '16px' }}>
                                  {Array.from({ length: question.total_option || 4 }).map((_, i) => {
                                    const isCorrect = correctAnswers.includes(String(i + 1));
                                    return (
                                      <div
                                        key={i}
                                        style={{
                                          padding: '10px 14px',
                                          background: isCorrect ? 'var(--green-light)' : 'var(--gray-50)',
                                          borderRadius: '6px',
                                          fontSize: '0.85rem',
                                          color: 'var(--gray-700)',
                                          border: isCorrect ? '1px solid var(--green)' : '1px solid var(--gray-200)'
                                        }}
                                      >
                                        <span style={{ fontWeight: 600, marginRight: '6px' }}>{String.fromCharCode(65 + i)}.</span>
                                        {getOption(question, i)}
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Answer & Details */}
                                <div style={{
                                  display: 'flex',
                                  gap: '16px',
                                  fontSize: '0.85rem',
                                  color: 'var(--gray-600)',
                                  flexWrap: 'wrap',
                                  padding: '12px',
                                  background: 'var(--gray-50)',
                                  borderRadius: '8px',
                                  marginBottom: '12px'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontWeight: 600 }}>Answer:</span>
                                    <span style={{ background: 'var(--green)', color: 'white', padding: '4px 10px', borderRadius: '4px', fontWeight: 600 }}>
                                      {getCorrectAnswerLabel(question.correct_ans || '')}
                                    </span>
                                  </div>
                                  <div><span style={{ fontWeight: 600 }}>Category:</span> {getCategoryLabel(question.exam_id, question.category_id)}</div>
                                  <div><span style={{ fontWeight: 600 }}>Set:</span> {question.set !== null && question.set !== undefined ? question.set : '—'}</div>
                                  <div><span style={{ fontWeight: 600 }}>Options:</span> {question.total_option}</div>
                                </div>

                                {/* Explanation */}
                                {question.explaination && (
                                  <div style={{ padding: '12px', background: 'var(--blue-light)', borderRadius: '8px', border: '1px solid var(--blue)' }}>
                                    <div style={{ fontWeight: 600, color: 'var(--blue)', marginBottom: '6px', fontSize: '0.85rem' }}>
                                      📚 Explanation:
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: 'var(--gray-700)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                      {question.explaination}
                                    </div>
                                  </div>
                                )}

                                {/* Delete Confirmation */}
                                {deleteConfirm === question.id && (
                                  <div style={{ marginTop: '16px', padding: '16px', background: 'var(--red-light)', borderRadius: '8px', border: '1px solid var(--red)' }}>
                                    <p style={{ color: 'var(--red)', fontWeight: 600, marginBottom: '12px' }}>
                                      ⚠️ Are you sure you want to delete this question?
                                    </p>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <button
                                        onClick={() => handleDelete(question.id!)}
                                        style={{ padding: '8px 16px', background: 'var(--red)', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                                      >
                                        Yes, Delete
                                      </button>
                                      <button
                                        onClick={() => setDeleteConfirm(null)}
                                        style={{ padding: '8px 16px', background: 'white', color: 'var(--gray-700)', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '700px', maxHeight: '80vh', overflow: 'auto', width: '90%' }}>
              <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '16px' }}>
                📥 Import Questions
              </h2>

              {importError && (
                <div style={{ padding: '12px', background: 'var(--red-light)', color: 'var(--red)', borderRadius: '6px', marginBottom: '16px' }}>
                  {importError}
                </div>
              )}

              <div style={{ marginBottom: '16px', color: 'var(--gray-700)' }}>
                <strong>{importData.length}</strong> questions found to import.
              </div>

              <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid var(--gray-200)', borderRadius: '6px', marginBottom: '16px' }}>
                {importData.slice(0, 10).map((q, index) => (
                  <div key={index} style={{ padding: '12px', borderBottom: '1px solid var(--gray-100)', fontSize: '0.85rem' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      Q{index + 1}: Exam {q.exam_id}, Cat {q.category_id}, {q.set !== null && q.set !== undefined ? `Set ${q.set}` : 'No set'}
                    </div>
                    <div style={{ color: 'var(--gray-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {q.question.substring(0, 100)}...
                    </div>
                  </div>
                ))}
                {importData.length > 10 && (
                  <div style={{ padding: '12px', color: 'var(--gray-500)', textAlign: 'center' }}>
                    ... and {importData.length - 10} more questions
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={closeImportModal}
                  style={{ padding: '10px 20px', background: 'white', color: 'var(--gray-700)', border: '1px solid var(--gray-300)', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmImport}
                  disabled={importing}
                  style={{ padding: '10px 20px', background: importing ? 'var(--gray-400)' : 'var(--green)', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: importing ? 'not-allowed' : 'pointer' }}
                >
                  {importing ? 'Importing...' : `Import ${importData.length} Questions`}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ManageQuestionsPage;